import { toast } from '@tempo-io/sui-sonner';
import { translateDynamicKey } from '@tempo-io/translations';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type SnackbarState = {
  message: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
};

type SnackbarContextType = {
  state?: SnackbarState;
  setMessage: Dispatch<SetStateAction<SnackbarState | undefined>>;
  onGlobalError: (error: unknown, variables: unknown) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  setMessage: () => {
    return;
  },
  onGlobalError: () => {
    return;
  },
});

type SnackbarProviderProps = {
  children: ReactNode;
};

export const useSetSnackbarGlobalError = () => useContext(SnackbarContext).onGlobalError;
export const useSetSnackbarMessage = () => useContext(SnackbarContext).setMessage;

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [state, setState] = useState<SnackbarState>();
  const { t } = useTranslation('translation');

  useEffect(() => {
    if (!state) return;
    const { message, severity = 'info' } = state;
    if (severity === 'error') toast.error(message, { duration: 6000 });
    else if (severity === 'warning') toast.warning(message, { duration: 6000 });
    else if (severity === 'success') toast.success(message, { duration: 6000 });
    else toast(message, { duration: 6000 });
  }, [state]);

  const handleGlobalError = (error: unknown) => {
    const responseData: Record<string, unknown> =
      (error as { response?: { data?: Record<string, unknown> } })?.response?.data ?? {};
    const message = (responseData.message as string) || '';

    let key: string;
    if (translateDynamicKey(t, `intake.serverErrors.${message}`, '')) {
      key = `intake.serverErrors.${message}`;
    } else if (translateDynamicKey(t, message, '')) {
      key = message;
    } else {
      key = 'intake.serverErrors.generic';
    }
    setState({
      message: translateDynamicKey(t, key, message, responseData),
      severity: 'error',
    });
  };

  const context = {
    state,
    setMessage: setState,
    onGlobalError: handleGlobalError,
  };

  return <SnackbarContext.Provider value={context}>{children}</SnackbarContext.Provider>;
};
